import { useState } from 'react';
import { hourLabel, slotLabel, type EditOutcome, type PeriodShift, type Site, type SiteQuarter } from '../lib/api';
import { dayLabel, monthShort, weekdayShort } from '../lib/dates';
import { navigate } from '../lib/router';
import { AddDialog, EDITABLE_STATUS, ShiftEditor } from './ShiftEdit';

type QuarterShift = SiteQuarter['days'][number]['shifts'][number];

/**
 * What a cell needs to be editable in place (D71). Without it the grid is read-only and a
 * cell is a link to its week, which is all the printed copy and any non-scheduler ever needs.
 */
export interface GridEditing {
  site: Site;
  editedBy: string;
  /** Called after a change lands, so the page can refetch and say what happened. */
  onChanged: (outcome: EditOutcome, what: string) => void;
}

/** A grid row carries no date of its own; the dialogs want a whole shift. */
function asShift(s: QuarterShift, date: string): PeriodShift {
  return { id: s.id, date, start: s.start, end: s.end, crossesMidnight: s.crossesMidnight, positionSlot: s.positionSlot, origin: s.origin, sourceLabel: null, physician: s.physician };
}

/** The schedule grid: positions down the side, the days given to it across the top (D64). */
export function ScheduleGrid({ siteId, slots, days, editing }: { siteId: string; slots: string[]; days: SiteQuarter['days']; editing?: GridEditing }) {
  // Which cell is open. A grid is a lot of cells, so exactly one editor at a time.
  const [open, setOpen] = useState<{ shift: PeriodShift; periodStart: string; status: string } | null>(null);
  const [adding, setAdding] = useState<{ date: string; periodStart: string; slot: string } | null>(null);

  // Every shift on screen, so a handover can find its other half — which may be the day before.
  const all = days.flatMap((d) => d.shifts.map((s) => asShift(s, d.date)));

  const openCell = (s: QuarterShift, date: string) => {
    if (!editing) return navigate(`/admin/sites/${siteId}/periods/${s.periodStart}`);
    setOpen({ shift: asShift(s, date), periodStart: s.periodStart, status: s.status });
  };

  // An empty cell can only be filled where the day is already planned: some other position
  // that day tells us which period the shift would belong to. A day with nothing on it at all
  // belongs to no week here, and guessing one would write into the wrong place.
  const addableDay = (d: SiteQuarter['days'][number]) => {
    const known = d.shifts.find((s) => EDITABLE_STATUS.has(s.status));
    return known ? known.periodStart : null;
  };

  return (
    <>
      <table className="grid month across">
        <thead>
          <tr>
            <th className="sticky name">Position</th>
            {days.map((d, i) => {
              const tuesday = weekdayShort(d.date) === 'Tue';
              // Which month a column is in, on the first one, on every handoff Tuesday, and
              // wherever the month turns. A quarter runs Dec 29 to Mar 29, so the grid used
              // to read "31" then "1" with nothing saying the year had changed — and it
              // scrolls sideways, so a reader can easily be looking at a stretch of bare
              // numbers. Every Tuesday means a month is never more than six columns away.
              const turned = i === 0 || d.date.slice(5, 7) !== days[i - 1].date.slice(5, 7);
              return (
                <th key={d.date} className={`daycol${tuesday ? ' handoff' : ''}`} scope="col" title={tuesday ? `${dayLabel(d.date)} · handoff` : dayLabel(d.date)}>
                  <span className="wd">{weekdayShort(d.date)}</span>
                  <span className="dnum">
                    {(turned || tuesday) && <span className="mon">{monthShort(d.date)}&nbsp;</span>}
                    {Number(d.date.slice(8, 10))}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {slots.map((slot) => (
            <tr key={slot}>
              <th className="sticky name" scope="row">{slotLabel(slot)}</th>
              {days.map((d) => {
                const tuesday = weekdayShort(d.date) === 'Tue';
                const shifts = d.shifts.filter((s) => s.positionSlot === slot);
                const fillable = editing && !shifts.length && addableDay(d);
                return (
                  <td key={d.date} className={`daycol${tuesday ? ' handoff' : ''}${shifts.length ? '' : ' empty'}`}>
                    {shifts.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        className="linklike cellname"
                        onClick={() => openCell(s, d.date)}
                        title={`${dayLabel(d.date)} · ${s.physician ? `${s.physician.firstName} ${s.physician.lastName}` : 'open'} · ${s.status}${editing ? ' · click to change' : ''}`}
                      >
                        {s.physician ? `${s.physician.firstName ? `${s.physician.firstName[0]}. ` : ''}${s.physician.lastName}` : '— open —'}
                        <span className="hours">{s.start && s.end ? `${hourLabel(s.start)}–${hourLabel(s.end)}` : 'on call'}</span>
                      </button>
                    ))}
                    {fillable && (
                      <button type="button" className="linklike celladd noprint" onClick={() => setAdding({ date: d.date, periodStart: fillable, slot })} title={`Add ${slotLabel(slot)} on ${dayLabel(d.date)}`} aria-label={`Add ${slotLabel(slot)} on ${dayLabel(d.date)}`}>
                        +
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && open && (
        <ShiftEditor
          site={editing.site}
          shift={open.shift}
          siblings={all}
          editedBy={editing.editedBy}
          periodStart={open.periodStart}
          weekStatus={open.status}
          onClose={() => setOpen(null)}
          onDone={(o, what) => { setOpen(null); editing.onChanged(o, what); }}
          onOpenWeek={() => navigate(`/admin/sites/${siteId}/periods/${open.periodStart}`)}
        />
      )}
      {editing && adding && (
        <AddDialog
          site={editing.site}
          dates={[adding.date]}
          existingDates={[adding.date]}
          editedBy={editing.editedBy}
          periodStart={adding.periodStart}
          slot={adding.slot}
          onClose={() => setAdding(null)}
          onDone={(o) => { setAdding(null); editing.onChanged(o, 'Added'); }}
        />
      )}
    </>
  );
}

/**
 * Split a run of days into printable pages, each break landing on a Tuesday so a tour is never
 * cut down the middle of itself (D64). A quarter is thirteen weeks, so it prints as three or
 * four sheets rather than one clipped one.
 */
export function printPages(days: SiteQuarter['days'], perPage = 21): SiteQuarter['days'][] {
  if (days.length <= perPage) return [days];
  const pages: SiteQuarter['days'][] = [];
  let rest = days;
  while (rest.length > perPage) {
    let cut = perPage;
    // Walk back to a Tuesday; a whole number of weeks keeps each sheet starting on a handoff.
    while (cut > 7 && weekdayShort(rest[cut]?.date ?? '') !== 'Tue') cut--;
    pages.push(rest.slice(0, cut));
    rest = rest.slice(cut);
  }
  if (rest.length) pages.push(rest);
  return pages;
}
